import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "form-inputs" }
const _hoisted_2 = { class: "form-buttons" }
const _hoisted_3 = { class: "left-buttons" }
const _hoisted_4 = { class: "right-buttons" }
const _hoisted_5 = { class: "left-buttons" }
const _hoisted_6 = { class: "right-buttons" }

import { computed, ref, useTemplateRef, onMounted, nextTick } from 'vue';

import { showError } from '@services/utils/dialog';
import NcButton from '@nextcloud/vue/components/NcButton';
import NcTextField from '@nextcloud/vue/components/NcTextField';

import AlbumCollaborators from './AlbumCollaborators.vue';

import { DateTime } from 'luxon';
import { t } from '@services/l10n';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as utils from '@services/utils/common';
import * as dav from '@services/dav';

import Send from 'vue-material-design-icons/Send.vue';
import AccountMultiplePlus from 'vue-material-design-icons/AccountMultiplePlus.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'AlbumForm',
},
  __name: 'AlbumForm',
  props: {
    album: {},
    displayBackButton: { type: Boolean }
  },
  emits: ["done", "back"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;

const nameInput = useTemplateRef<VueHTMLComponent>('nameInput');

const showCollaboratorView = ref(false);
const albumName = ref('');
const albumLocation = ref('');
const loading = ref(false);
const abort = useAbort();

/** Whether sharing is enabled. */
const editMode = computed(() => Boolean(props.album));
const saveText = computed(() => (editMode.value ? t('memories', 'Save') : t('memories', 'Create album')));

/** Whether sharing is enabled. */
const sharingEnabled = computed(() => true); // todo

onMounted(() => {
  if (editMode.value) {
    albumName.value = props.album.basename;
    albumLocation.value = props.album.location;
  }
  nextTick(() => {
    nameInput.value?.$el.getElementsByTagName('input')[0].focus();
  });
});

function submit(collaborators: any[] = []) {
  if (!albumName.value.trim() || loading.value) {
    return;
  }

  // Validate the album name, it shouldn't contain any slash
  if (albumName.value.includes('/')) {
    showError(t('memories', 'Invalid album name; should not contain any slashes.'));
    return;
  }

  if (editMode.value) {
    handleUpdateAlbum();
  } else {
    handleCreateAlbum(collaborators);
  }
}

async function handleCreateAlbum(collaborators: any[] = []) {
  const signal = abort.renew();
  try {
    loading.value = true;
    let album = {
      basename: albumName.value,
      filename: `/photos/${utils.uid}/albums/${albumName.value}`,
      nbItems: 0,
      location: albumLocation.value,
      lastPhoto: -1,
      date: DateTime.now().toFormat('MMMM yyyy'),
      collaborators,
    };
    await dav.createAlbum(album.basename, { signal });
    signal.throwIfAborted();

    if (albumLocation.value !== '' || collaborators.length !== 0) {
      album = await dav.updateAlbum(
        album,
        {
          albumName: albumName.value,
          properties: {
            location: albumLocation.value,
            collaborators,
          },
        },
        { signal },
      );
      signal.throwIfAborted();
    }

    emit('done', { album });
  } catch (error) {
    if (isAbortError(error)) return;
    console.error(error);
    showError(t('memories', 'Failed to create album.'));
  } finally {
    if (!signal.aborted) loading.value = false;
  }
}

async function handleUpdateAlbum() {
  const signal = abort.renew();
  try {
    loading.value = true;
    let album = { ...props.album };
    if (album.basename !== albumName.value) {
      album = await dav.renameAlbum(album, album.basename, albumName.value, { signal });
      signal.throwIfAborted();
    }
    if (album.location !== albumLocation.value) {
      album.location = await dav.updateAlbum(
        album,
        {
          albumName: album.basename,
          properties: { location: albumLocation.value },
        },
        { signal },
      );
      signal.throwIfAborted();
    }
    emit('done', { album });
  } catch (error) {
    if (isAbortError(error)) return;
    console.error(error);
    showError(t('memories', 'Failed to update album.'));
  } finally {
    if (!signal.aborted) loading.value = false;
  }
}

function back() {
  emit('back');
}

return (_ctx: any,_cache: any) => {
  return (!showCollaboratorView.value)
    ? (_openBlock(), _createElementBlock("form", {
        key: 0,
        class: "album-form",
        onSubmit: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"]))
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_unref(NcTextField), {
            ref_key: "nameInput",
            ref: nameInput,
            type: "text",
            name: "name",
            autofocus: "true",
            modelValue: albumName.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((albumName).value = $event)),
            required: true,
            label: _unref(t)('memories', 'Album name'),
            "label-visible": true
          }, null, 8, ["modelValue", "label"]),
          _createVNode(_unref(NcTextField), {
            name: "location",
            type: "text",
            modelValue: albumLocation.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((albumLocation).value = $event)),
            label: _unref(t)('memories', 'Location'),
            "label-visible": true
          }, null, 8, ["modelValue", "label"])
        ]),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, [
            (__props.displayBackButton)
              ? (_openBlock(), _createBlock(_unref(NcButton), {
                  key: 0,
                  "aria-label": _unref(t)('memories', 'Go back to the previous view.'),
                  variant: "tertiary",
                  onClick: back
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('memories', 'Back')), 1)
                  ]),
                  _: 1
                }, 8, ["aria-label"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("span", _hoisted_4, [
            (sharingEnabled.value && !editMode.value)
              ? (_openBlock(), _createBlock(_unref(NcButton), {
                  key: 0,
                  "aria-label": _unref(t)('memories', 'Go to the add collaborators view.'),
                  variant: "secondary",
                  disabled: albumName.value.trim() === '' || loading.value,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (showCollaboratorView.value = true))
                }, {
                  icon: _withCtx(() => [
                    _createVNode(AccountMultiplePlus)
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(" " + _toDisplayString(_unref(t)('memories', 'Add collaborators')), 1)
                  ]),
                  _: 1
                }, 8, ["aria-label", "disabled"]))
              : _createCommentVNode("", true),
            _createVNode(_unref(NcButton), {
              "aria-label": saveText.value,
              variant: "primary",
              disabled: !albumName.value.trim() || loading.value,
              onClick: _cache[3] || (_cache[3] = ($event: any) => (submit()))
            }, {
              icon: _withCtx(() => [
                (loading.value)
                  ? (_openBlock(), _createBlock(XLoadingIcon, { key: 0 }))
                  : (_openBlock(), _createBlock(Send, { key: 1 }))
              ]),
              default: _withCtx(() => [
                _createTextVNode(" " + _toDisplayString(saveText.value), 1)
              ]),
              _: 1
            }, 8, ["aria-label", "disabled"])
          ])
        ])
      ], 32))
    : (_openBlock(), _createBlock(AlbumCollaborators, {
        key: 1,
        "album-name": albumName.value,
        "allow-public-link": false,
        collaborators: []
      }, {
        default: _withCtx(({ collaborators }) => [
          _createElementVNode("span", _hoisted_5, [
            _createVNode(_unref(NcButton), {
              "aria-label": _unref(t)('memories', 'Back to the new album form.'),
              variant: "tertiary",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (showCollaboratorView.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('memories', 'Back')), 1)
              ]),
              _: 1
            }, 8, ["aria-label"])
          ]),
          _createElementVNode("span", _hoisted_6, [
            _createVNode(_unref(NcButton), {
              "aria-label": saveText.value,
              variant: "primary",
              disabled: albumName.value.trim() === '' || loading.value,
              onClick: ($event: any) => (submit(collaborators))
            }, {
              icon: _withCtx(() => [
                (loading.value)
                  ? (_openBlock(), _createBlock(XLoadingIcon, { key: 0 }))
                  : (_openBlock(), _createBlock(Send, { key: 1 }))
              ]),
              default: _withCtx(() => [
                _createTextVNode(" " + _toDisplayString(saveText.value), 1)
              ]),
              _: 1
            }, 8, ["aria-label", "disabled", "onClick"])
          ])
        ]),
        _: 1
      }, 8, ["album-name"]))
}
}

})